import { Avatar, Box, BoxProps, Typography } from '@mui/material';
import { useMemo } from 'react';

type PlanName = 'Business' | 'Enterprise' | 'Standard' | 'Starter';

export type ProLogoWithPlanProps = Pick<BoxProps, 'sx'> & {
    src?: string;
    alt?: string;
    fallbackLetter?: string;
    planName?: string | null;
    size?: 'sm' | 'md' | 'lg';
};

const PLAN_COLORS: Record<PlanName, string> = {
    Business: '#4239B0',
    Enterprise: '#000',
    Standard: '#2F80ED',
    Starter: '#706D85',
};

const normalizePlanName = (planName?: string | null): PlanName | null => {
    if (!planName) {
        return null;
    }

    const normalized = planName.trim().toLowerCase();

    switch (normalized) {
        case 'business':
            return 'Business';
        case 'enterprise':
            return 'Enterprise';
        case 'standard':
            return 'Standard';
        case 'starter':
            return 'Starter';
        default:
            return null;
    }
};

const ProLogoWithPlan: React.FC<ProLogoWithPlanProps> = ({
    src,
    alt,
    fallbackLetter = 'C',
    planName,
    size = 'sm',
    sx,
}) => {
    const { wrapperWidth, avatarSize, badgeHeight, badgeFontSize } = useMemo(() => {
        switch (size) {
            case 'lg':
                return {
                    wrapperWidth: 82,
                    avatarSize: 64,
                    badgeHeight: 24,
                    badgeFontSize: '12px',
                };
            case 'md':
                return {
                    wrapperWidth: 72,
                    avatarSize: 52,
                    badgeHeight: 22,
                    badgeFontSize: '11px',
                };
            case 'sm':
            default:
                return {
                    wrapperWidth: 64,
                    avatarSize: 40,
                    badgeHeight: 20,
                    badgeFontSize: '10px',
                };
        }
    }, [size]);

    const resolvedPlanName = normalizePlanName(planName);
    const badgeLabel = resolvedPlanName ?? 'No plan';
    const badgeColor = resolvedPlanName ? PLAN_COLORS[resolvedPlanName] : '#CBCBCB';

    return (
        <Box
            sx={{
                width: wrapperWidth,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                ...sx,
            }}
        >
            <Avatar
                src={src}
                alt={alt}
                sx={{
                    position: 'relative',
                    zIndex: 1,
                    width: avatarSize,
                    height: avatarSize,
                    border: `2px solid ${badgeColor}`,
                    fontWeight: 700,
                    fontSize: size === 'lg' ? '22px' : '18px',
                    textTransform: 'uppercase',
                }}
            >
                {fallbackLetter}
            </Avatar>

            <Box
                sx={{
                    position: 'relative',
                    zIndex: 2,
                    mt: '-8px',
                    minWidth: '68px',
                    maxWidth: wrapperWidth,
                    height: badgeHeight,
                    px: 0.75,
                    borderRadius: '12px',
                    backgroundColor: badgeColor,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    overflow: 'hidden',
                    border: theme => `1px solid ${theme.palette.neutralGray.main}`,
                }}
            >
                <Typography
                    sx={{
                        color: '#FFF',
                        fontWeight: 700,
                        fontSize: badgeFontSize,
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        lineHeight: 1,
                    }}
                >
                    {badgeLabel}
                </Typography>
            </Box>
        </Box>
    );
};

export default ProLogoWithPlan;
